import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatWidgetPosition } from './use-chat-widget-position';

// The devtools takes the four corners only. It is 780px wide, so the chat's `-center` zones would
// put it under both side panels at once with no edge to escape to.
export type DevtoolsPosition = Extract<ChatWidgetPosition, 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>;
const DEFAULT_POSITION: DevtoolsPosition = 'bottom-right';
const POSITIONS: readonly DevtoolsPosition[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
export const isDevtoolsCorner = (value: unknown): value is DevtoolsPosition => typeof value === 'string' && (POSITIONS as readonly string[]).includes(value);

/** Which corner a release at (x, y) lands in. */
export const snapToCorner = (x: number, y: number): DevtoolsPosition => {
  const col = x < window.innerWidth / 2 ? 'left' : 'right';
  const row = y < window.innerHeight / 2 ? 'top' : 'bottom';
  return `${row}-${col}`;
};
const flipCol = (p: DevtoolsPosition): DevtoolsPosition => (p.endsWith('left') ? p.replace('left', 'right') : p.replace('right', 'left')) as DevtoolsPosition;

/**
 * The corner to render in: the chosen one, or the next free corner when the chat already claims it.
 * Callers pass `taken` as null when the chat isn't corner-anchored. The stored preference is left
 * alone, so the panel returns to the chosen corner once the chat leaves it.
 */
export const resolveCorner = (preferred: DevtoolsPosition, taken: DevtoolsPosition | null): DevtoolsPosition => {
  if (taken !== preferred) return preferred;
  // Sideways first: it keeps the panel on the edge the eye is already on.
  return flipCol(preferred);
};
type DevtoolsPositionState = {
  position: DevtoolsPosition;
  setPosition: (next: DevtoolsPosition) => void;
};

// Separate from the chat's own corner: the two float independently, and the point of moving the
// devtools is to get it off whatever the chat is covering.
const useStore = create<DevtoolsPositionState>()(persist(set => ({
  position: DEFAULT_POSITION,
  setPosition: next => set({
    position: next
  })
}), {
  name: 'percy.devtools-position',
  merge: (persisted, current) => {
    const position = isDevtoolsCorner((persisted as Partial<DevtoolsPositionState>)?.position) ? (persisted as DevtoolsPositionState).position : DEFAULT_POSITION;
    return {
      ...current,
      position
    };
  }
}));
const useDevtoolsPosition = () => {
  return useStore();
};
export default useDevtoolsPosition;